<template>
  <div
    class="responsive_layout"
    :class="{ sticky_actions: stickyActions, [theme]: true }"
  >
    <div
      class="header_wrapper"
      :class="{ show_scroll: showTopShadow }"
    >
      <div class="header">
        <div class="start">
          <slot name="header_start">
            <div
              v-if="showKaiaLogo"
              class="kaia_logo"
            />
          </slot>
        </div>
        <div class="middle">
          <slot name="header_middle" />
        </div>
        <div class="end">
          <LanguageSwitcher
            v-if="showLangPicker"
            short
          />
          <slot name="header_end" />
        </div>
        <div
          v-if="progress !== null"
          class="progress_background"
        />
        <div
          v-if="progress !== null"
          class="progress"
          :style="`width: ${progress * 100}%;`"
        />
      </div>
    </div>
    <div class="content_wrapper">
      <div class="content">
        <slot />
      </div>
    </div>
    <div
      v-if="$slots.actions"
      class="actions"
      :class="{ show_scroll: showBottomShadow }"
    >
      <slot name="actions" />
    </div>
    <div class="content_bottom_space" />
    <div class="layout_bottom_space" />
    <slot name="footer" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { debounce } from '@shared/utils.js'
import LanguageSwitcher from '@shared/components/LanguageSwitcher.vue'
import useResponsiveness from '@shared/composables/useResponsiveness.js'

const props = defineProps({
  showKaiaLogo: {
    type: Boolean,
    default: true,
  },
  showLangPicker: {
    type: Boolean,
    default: true,
  },
  stickyActions: {
    type: Boolean,
    default: false,
  },
  progress: {
    type: Number,
    default: null,
  },
  theme: {
    type: String,
    default: 'default',
    validator: (val) =>
      ['default', 'blue-background', 'blue-actions'].includes(val),
  },
})

const responsiveness = useResponsiveness()
const showTopShadow = ref(false)
const showBottomShadow = ref(false)
const scrollDeadZone = 5

const handleContentWrapperScroll = () => {
  showTopShadow.value = window.scrollY > scrollDeadZone

  const bottomSpaceHeight = ['sm', 'md'].includes(responsiveness.size.value)
    ? 0
    : 48
  showBottomShadow.value =
    document.documentElement.scrollHeight -
      window.scrollY -
      window.innerHeight -
      bottomSpaceHeight >
    scrollDeadZone
}

const debouncedHandleContentWrapperScroll = debounce(
  handleContentWrapperScroll,
  5,
)

const themeClass = computed(() => props.theme)

watch(themeClass, (newTheme, oldTheme) => {
  if (oldTheme) document.body.classList.remove(oldTheme)
  if (newTheme) document.body.classList.add(newTheme)
})

onMounted(() => {
  handleContentWrapperScroll()
  window.addEventListener('resize', debouncedHandleContentWrapperScroll)
  window.addEventListener('scroll', debouncedHandleContentWrapperScroll)
  document.body.classList.add(themeClass.value)
})
onUnmounted(() => {
  window.removeEventListener('resize', debouncedHandleContentWrapperScroll)
  window.removeEventListener('scroll', debouncedHandleContentWrapperScroll)
  document.body.classList.remove(themeClass.value)
})
</script>

<style lang="scss" scoped>
.responsive_layout {
  width: 100%;
  min-height: 100dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @include media-up(md) {
    background-color: $kds-color-background-subtle;
  }

  .header_wrapper {
    width: 100%;
    background-color: $kds-color-background-base;
    z-index: 10;
    position: sticky;
    top: 0;
    // respect top space on iOS devices
    padding-top: env(safe-area-inset-top);

    &.show_scroll {
      box-shadow: 0 1.5px 0 0 $kds-color-background-interactive-subtle-hover;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 $kds-space-2_5x;
      gap: $kds-space-1x;
      height: 46px;

      .start,
      .middle,
      .end {
        display: flex;
      }

      .middle {
        flex-grow: 1;
      }

      .kaia_logo {
        background-image: url('@shared/assets/img/kaia_text_logo.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        height: 14px;
        width: 100px;

        @include media-up(md) {
          height: $kds-space-3x;
          width: 145px;
        }
      }

      .progress_background {
        position: absolute;
        left: 0;
        width: 100%;
        bottom: -4px;
        height: 4px;
        background-color: $kds-color-background-subtle;
      }

      .progress {
        position: absolute;
        left: 0;
        bottom: -4px;
        height: 4px;
        background-color: $kds-color-background-interactive-accent;
      }

      @include media-up(md) {
        max-width: 796px;
        margin: 0 auto;
        height: 70px;
      }
    }
  }

  .content_wrapper {
    padding: $kds-space-2_5x;

    @include media-up(md) {
      padding: 0;
    }

    .content {
      @include media-up(md) {
        width: calc($br-m - $kds-space-3x);
        box-sizing: border-box;
        margin: $kds-space-4x auto 0 auto;
        padding: $kds-space-4x $kds-space-6x $kds-space-2x $kds-space-6x;
        border-radius: $kds-shape-rounded-large $kds-shape-rounded-large 0 0;
        background-color: $kds-color-background-base;
      }
    }
  }

  .actions {
    padding: $kds-space-1x $kds-space-2_5x $kds-space-2_5x;
    background-color: $kds-color-background-base;
    display: flex;
    flex-direction: column;
    gap: $kds-space-2x;

    @include media-up(md) {
      width: calc($br-m - $kds-space-3x);
      box-sizing: border-box;
      margin: 0 auto 0 auto;
      padding: $kds-space-2x $kds-space-6x;
    }
  }

  .content_bottom_space {
    background-color: $kds-color-background-base;
    // respect bottom space on iOS devices
    height: max(0px, calc(env(safe-area-inset-bottom) - #{$kds-space-2_5x}));

    @include media-up(md) {
      width: calc($br-m - $kds-space-3x);
      height: $kds-space-4x;
      flex-grow: 0;
      box-sizing: border-box;
      margin: 0 auto;
      border-radius: 0 0 $kds-shape-rounded-large $kds-shape-rounded-large;
    }
  }

  .layout_bottom_space {
    flex-grow: 1;

    @include media-up(md) {
      min-height: $kds-space-2x;
    }
  }

  &.sticky_actions {
    display: flex;
    flex-direction: column;

    .content_wrapper {
      flex-grow: 1;

      @include media-up(md) {
        flex-grow: 0;
      }
    }

    .actions {
      position: sticky;
      // respect bottom space on iOS devices
      bottom: max(0px, calc(env(safe-area-inset-bottom) - #{$kds-space-2_5x}));

      &.show_scroll {
        box-shadow: 0 -1.5px 0 0 $kds-color-background-interactive-subtle-hover;
      }
    }

    .content_bottom_space {
      position: sticky;
      bottom: 0;

      @include media-up(md) {
        position: initial;
      }
    }

    .layout_bottom_space {
      flex-grow: 0;
    }
  }

  &.blue-background {
    .header_wrapper {
      background-color: $kds-color-background-blue-strong;

      @include media-up(md) {
        background-color: $kds-color-background-base;
      }
    }

    .content_wrapper {
      background-color: $kds-color-background-blue-strong;

      @include media-up(md) {
        background-color: unset;
      }

      .content {
        @include media-up(md) {
          background-color: $kds-color-background-blue-strong;
        }
      }
    }

    .actions {
      background-color: $kds-color-background-blue-strong;
    }

    .content_bottom_space {
      background-color: $kds-color-background-blue-strong;
    }
  }

  &.blue-actions {
    .actions {
      background-color: $kds-color-background-blue-strong;
    }

    .content_bottom_space {
      background-color: $kds-color-background-blue-strong;
    }
  }
}
</style>

<style lang="scss">
body.default {
  @include media-up(md) {
    background-color: $kds-color-background-subtle;
  }
}

body.blue-background {
  background-color: $kds-color-background-blue-strong;

  @include media-up(md) {
    background-color: $kds-color-background-subtle;
  }
}
</style>
